import { AfterViewInit, Component, ElementRef, inject, Input, OnDestroy, ViewChild,
    ChangeDetectionStrategy
} from "@angular/core";
import { MonacoEditorLoaderService, MonacoModule } from "../../services/monaco-editor-loader.service";

type MonacoDiffEditor = import("monaco-editor/esm/vs/editor/editor.api.js").editor.IStandaloneDiffEditor;
type MonacoDisposable = import("monaco-editor/esm/vs/editor/editor.api.js").IDisposable;
type MonacoTextModel = import("monaco-editor/esm/vs/editor/editor.api.js").editor.ITextModel;

@Component({
    selector: "app-text-diff",
    templateUrl: "./text-diff.component.html",
    styleUrls: ["./text-diff.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TextDiffComponent implements AfterViewInit, OnDestroy {

    private loader = inject(MonacoEditorLoaderService);
    private _fileName = "";
    private _left = "";
    private _right = "";
    private _leftFileName = "";
    private _rightFileName = "";
    private _sideBySide = true;
    private destroyed = false;
    private revealFirstChangePending = false;
    private updateScheduled = false;
    private monaco: MonacoModule;
    private diffEditor: MonacoDiffEditor;
    private diffUpdatedListener: MonacoDisposable;
    private originalModel: MonacoTextModel;
    private modifiedModel: MonacoTextModel;

    @ViewChild("container", {static: true}) container!: ElementRef<HTMLDivElement>;

    @Input()
    set leftFileName(value: string) {
        this._leftFileName = value ?? "";
        this.update();
    }

    get leftFileName(): string {
        return this._leftFileName;
    }

    @Input()
    set rightFileName(value: string) {
        this._rightFileName = value ?? "";
        this.update();
    }

    get rightFileName(): string {
        return this._rightFileName;
    }

    @Input()
    set fileName(value: string) {
        this._fileName = value ?? "";
        this.update();
    }

    get fileName(): string {
        return this._fileName;
    }

    @Input()
    set left(value: string) {
        this._left = value ?? "";
        this.update();
    }

    get left(): string {
        return this._left;
    }

    @Input()
    set right(value: string) {
        this._right = value ?? "";
        this.update();
    }

    get right(): string {
        return this._right;
    }

    @Input()
    set sideBySide(value: boolean) {
        this._sideBySide = value;
        this.update();
    }

    get sideBySide(): boolean {
        return this._sideBySide;
    }

    @Input()
    revealFirstChange = false;

    async ngAfterViewInit(): Promise<void> {
        let monaco = await this.loader.load("json");
        if (this.destroyed) {
            return;
        }

        this.monaco = monaco;
        this.diffEditor = this.monaco.editor.createDiffEditor(this.container.nativeElement, {
            automaticLayout: true,
            diffAlgorithm: "advanced",
            diffWordWrap: "on",
            enableSplitViewResizing: false,
            minimap: {enabled: false},
            originalAriaLabel: this._leftFileName || "Linke Version",
            modifiedAriaLabel: this._rightFileName || "Rechte Version",
            originalEditable: false,
            readOnly: true,
            renderOverviewRuler: false,
            renderSideBySide: this._sideBySide,
            scrollBeyondLastLine: false,
            splitViewDefaultRatio: 0.5,
            useInlineViewWhenSpaceIsLimited: false,
            wordWrap: "on"
        });
        this.diffUpdatedListener = this.diffEditor.onDidUpdateDiff(() => this.revealFirstChangeIfRequested());
        this.updateMonaco();
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        this.diffUpdatedListener?.dispose();
        this.diffEditor?.dispose();
        this.originalModel?.dispose();
        this.modifiedModel?.dispose();
        this.diffEditor = null;
        this.originalModel = null;
        this.modifiedModel = null;
        this.diffUpdatedListener = null;
    }

    setWidth(width: number): void {
        if (this.container) {
            this.container.nativeElement.style.width = width + "px";
            this.container.nativeElement.style.maxWidth = width + "px";
            this.layoutMonaco();
        }
    }

    private update(): void {
        if (this.updateScheduled) {
            return;
        }

        this.updateScheduled = true;
        queueMicrotask(() => {
            this.updateScheduled = false;
            if (!this.destroyed) {
                this.updateMonaco();
            }
        });
    }

    private updateMonaco(): void {
        if (this.diffEditor == null) {
            return;
        }

        this.diffEditor.updateOptions({renderSideBySide: this._sideBySide});
        this.diffEditor.getOriginalEditor().updateOptions({ariaLabel: this._leftFileName || "Linke Version"});
        this.diffEditor.getModifiedEditor().updateOptions({ariaLabel: this._rightFileName || "Rechte Version"});
        this.revealFirstChangePending = this.revealFirstChange;

        let language = this.getMonacoLanguage();
        if (this.originalModel == null || this.modifiedModel == null) {
            this.originalModel = this.monaco.editor.createModel(this._left, language);
            this.modifiedModel = this.monaco.editor.createModel(this._right, language);
            this.diffEditor.setModel({original: this.originalModel, modified: this.modifiedModel});
        }
        else {
            let modelChanged = this.originalModel.getLanguageId() != language || this.originalModel.getValue() != this._left || this.modifiedModel.getValue() != this._right;
            if (modelChanged) {
                this.diffEditor.setModel(null);
            }
            if (this.originalModel.getLanguageId() != language) {
                this.monaco.editor.setModelLanguage(this.originalModel, language);
                this.monaco.editor.setModelLanguage(this.modifiedModel, language);
            }
            if (this.originalModel.getValue() != this._left) {
                this.originalModel.setValue(this._left);
            }
            if (this.modifiedModel.getValue() != this._right) {
                this.modifiedModel.setValue(this._right);
            }
            if (modelChanged) {
                this.diffEditor.setModel({original: this.originalModel, modified: this.modifiedModel});
            }
        }
        this.layoutMonaco();
    }

    private revealFirstChangeIfRequested(): void {
        if (!this.revealFirstChangePending) {
            return;
        }

        let firstChange = this.diffEditor.getLineChanges()?.[0];
        if (firstChange != null) {
            this.revealFirstChangePending = false;
            this.diffEditor.getModifiedEditor().revealLineInCenter(Math.max(1, firstChange.modifiedStartLineNumber), this.monaco.editor.ScrollType.Immediate);
        }
    }

    private getMonacoLanguage(): string {
        let fileName = this._fileName.toLowerCase();
        return fileName.endsWith(".json") || fileName.endsWith(".json_inactive") ? "json" : "plaintext";
    }

    private layoutMonaco(): void {
        if (this.diffEditor != null) {
            requestAnimationFrame(() => this.diffEditor?.layout());
        }
    }
}
